import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {TYPES_TO_DISPLAY_NAMES} from 'analysis-tool/AnalysisToolConstants';
import {COLOR_PALETTES} from 'analysis-tool/ui/charting/ChartingConstants';
import {DiscreteColorPalette} from 'analysis-tool/ui/charting/ChartingTypes';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FilterStepFilterConfig from 'data-frames/filters/components/FilterStepFilterConfig';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import AnalysisFormulaEditor from 'pipelines/formula/components/AnalysisFormulaEditor';
import {AnalysisFormulaParsingError} from 'pipelines/formula/Constants';
import formulaTextToFormulaAstJson from 'pipelines/formula/formulaTextToFormulaAstJson';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import IsInputXToggleGroup from 'pipelines/interpolation/IsInputXToggleGroup';
import {
  GetPipelineStepShape,
  GetTaggedUnionTagValues,
  PipelineJsonSpecToTsType,
} from 'pipelines/PipelineTypes';
import {InputDataFrames} from 'pipelines/StepDataUtils';
import {
  PipelineConfigJsonSpec,
  GetPipelineConfigSpecType,
  StepConfigShape,
  EnumOptions,
} from 'pipelines/StepShapeUtils';
import UserDefinedFunctionEditor from 'pipelines/user-defined-function/UserDefinedFunctionEditor';
import {capitalize, constantCaseToWords} from 'util/NameUtil';
import {AllKeys, assertJSONObject, isJSONArray, JSONObject, JSONValue} from 'util/Types';

import ColumnDragAndDrop from '../editors/ColumnDragAndDrop';
import DefineTemplateVariablesEditor from '../editors/custom-editors/DefineTemplateVariablesEditor';
import JoinEditor from '../editors/custom-editors/JoinEditor';
import LookUpConfigSectionEditor from '../editors/custom-editors/LookupColumnAndBy';
import LookUpFieldEditor from '../editors/custom-editors/LookUpFieldEditor';
import UnionEditor from '../editors/custom-editors/UnionEditor';
import SearchEditor from '../editors/SearchEditor';
import {
  BANNER_KEY,
  ConfigDataFrameInfo,
  ConfigDataFramesInfos,
  ConfigShapePath,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  FieldUISchemaBanner,
  HAS_BACKEND_FIELD_GENERATOR,
  HIDDEN_FIELDS_KEY,
  isDiscreteColorPalette,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaV2,
  TAB_ORDER_UI_SCHEMA_KEY,
} from '../StepConfigEditorTypes';
import {BAR_CHART_CONFIG_SECTIONS, BAR_CHART_STYLE_SECTIONS} from './chart-schemas/BarChartSchema';
import {BOX_PLOT_CONFIG_SECTIONS, BOX_PLOT_STYLE_SECTIONS} from './chart-schemas/BoxPlotSchema';
import {createChartTypeSchema} from './chart-schemas/ChartSchemaHelpers';
import {HEATMAP_CONFIG_SECTIONS} from './chart-schemas/HeatmapSchema';
import {LINE_CHART_CONFIG_SECTIONS, LINE_CHART_STYLE_SECTIONS} from './chart-schemas/LineChartSchema';
import {PIE_CHART_CONFIG_SECTIONS, PIE_CHART_STYLE_SECTIONS} from './chart-schemas/PieChartSchema';
import {
  SCATTER_CHART_CONFIG_SECTIONS,
  SCATTER_CHART_STYLE_SECTIONS,
} from './chart-schemas/ScatterChartSchema';

type OnChangeConfigMiddlewareFn<
  JsonSpec extends PipelineConfigJsonSpec,
  StepType extends PipelineStepType = PipelineStepType,
  StepVersion extends number = number,
> = (
  newConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>,
  oldConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>,
  dataFramesInfos: ConfigDataFramesInfos<StepType, StepVersion>
) => PipelineJsonSpecToTsType<JsonSpec, 'draft'>;

interface SharedComponentUISchema {
  displayName?: string;
  infoText?: string;
}

interface SharedComponentUISchemaV2 extends SharedComponentUISchema {
  configShapePath?: Array<string>;
}

interface CustomComponentUISchema<JsonSpec extends PipelineConfigJsonSpec> extends SharedComponentUISchema {
  [CUSTOM_COMPONENT_KEY]: (props: {
    config: PipelineJsonSpecToTsType<JsonSpec, 'draft'>;
    onChangeConfig: (newConfig: PipelineJsonSpecToTsType<JsonSpec, 'draft'>) => void;
    disabled?: boolean;
  }) => JSX.Element;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: OnChangeConfigMiddlewareFn<JsonSpec>;
}

interface StandardComponentUISchema<JsonSpec extends PipelineConfigJsonSpec> extends SharedComponentUISchema {
  fieldSettings?: _GetJsonSpecUISchemaShape<JsonSpec>;
}

interface StandardComponentUISchemaV2<JsonSpec extends PipelineConfigJsonSpec>
  extends SharedComponentUISchemaV2 {
  fieldSettings?: _GetJsonSpecUISchemaShapeV2<JsonSpec>;
}

// See docstring for GetConfigShapeUISchema
type _GetJsonSpecUISchemaShape<JsonSpec extends PipelineConfigJsonSpec> = JsonSpec extends {
  kind: 'shape';
  fields: infer Fields extends Record<string, PipelineConfigJsonSpec>;
}
  ? {
      /**
       * Ordered array of variable names.
       *
       * Variable names that are not found in this array will be rendered in their original order after any
       * variables that _are_ in this array.
       */
      [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<keyof Fields>;
      /**
       * Variables that should be hidden, meaning that they will not be configurable from the default step
       * config view.
       */
      [HIDDEN_FIELDS_KEY]?: ReadonlyArray<keyof Fields>;
      /**
       * A function that is called whenever the config changes.
       *
       * One example usage is to set the default of a field based on the values of other fields.
       *
       * It takes in the new config, the old config, and the data frame spec, and should return whatever
       * config should be set instead of the new config.
       */
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: OnChangeConfigMiddlewareFn<JsonSpec>;
      // Allows adding a banner to the top of the step.
      [BANNER_KEY]?: FieldUISchemaBanner;
      // Indicates that step has a backend API to update step configuration.
      [HAS_BACKEND_FIELD_GENERATOR]?: boolean;
    } & {
      // Must be assignable to `ShapeUISchema`
      [FieldName in keyof Fields]?:
        | StandardComponentUISchema<Fields[FieldName]>
        | CustomComponentUISchema<Fields[FieldName]>;
    }
  : JsonSpec extends {kind: 'array'; elementSpec: infer ElementSpec extends PipelineConfigJsonSpec}
    ? _GetJsonSpecUISchemaShape<ElementSpec>
    : JsonSpec extends {
          kind: 'taggedUnion';
          tagKey: infer TagKey extends string;
          choices: ReadonlyArray<infer Choice extends GetPipelineConfigSpecType<'shape'>>;
        }
      ? {
          // Must be assignable to `TaggedUnionUISchema`
          placeholder?: string;
          infoText?: string;
          tagKeyDisplayNames?: {[TagChoice in GetTaggedUnionTagValues<JsonSpec>]?: string};
          choiceSettings?: _GetJsonSpecUISchemaShape<Choice>;
          /**
           * Ordered array of variable names. It should not include the tag key, since that is always rendered
           * first.
           *
           * Variable names that are not found in this array will be rendered in their original order after any
           * variables that _are_ in this array.
           */
          [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<Exclude<AllKeys<Choice['fields']>, TagKey>>;
          /**
           * Variables that should be hidden, meaning that they will not be configurable from the default step
           * config view.
           */
          [HIDDEN_FIELDS_KEY]?: ReadonlyArray<Exclude<AllKeys<Choice['fields']>, TagKey>>;
          /**
           * A function that is called whenever the config changes.
           *
           * One example usage is to set the default of a field based on the values of other fields.
           *
           * It takes in the new config, the old config, and the data frame spec, and should return whatever
           * config should be set instead of the new config.
           */
          [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: OnChangeConfigMiddlewareFn<JsonSpec>;
        }
      : JsonSpec extends {kind: 'enum'; choices: ReadonlyArray<infer Choice extends string | number>}
        ? // Must be assignable to `EnumUISchema`
          {displayNames?: {[key in Choice]?: string}; defaultValue?: Choice; placeholder?: string}
        : JsonSpec extends {kind: 'enum'; choices: ReadonlyArray<EnumOptions>}
          ? // Must be assignable to `EnumUISchema`
            {defaultValue?: string | number; placeholder?: string}
          : never;
type _GetConfigShapeUISchema<ConfigShape extends StepConfigShape> = _GetJsonSpecUISchemaShape<{
  kind: 'shape';
  required: true;
  fields: ConfigShape;
}>;

/**
 * Get the UI schema for a step config shape.
 *
 * This is a recursive function that traverses the shape of the step config and returns a UI schema based
 * on the types of the encountered shapes.
 *
 * All UI schema fields are optional.
 *
 * Also supports specifying a custom component for a field, instead of inferring the schema for the field
 * from the spec.
 *
 * Behavior:
 *   * `kind: 'shape'` -> `{displayName: string; fieldSettings: Record<FieldName, (Recursively applied to field type)>}`
 *   * `kind: 'array'` -> `(Recursively applied to array's elementSpec)`
 *   * `kind: 'taggedUnion'` -> `{tagKeyDisplayNames: Record<TagKey, string>; choiceSettings: (Recursively applied to choice types)>}`
 *     Note that all choices will be combined into the same UI Schema, rather than giving each choice its
 *     own schema. This is for simplicity since usually the fields in each choice are relatively similar.
 *   * `kind: 'enum'` -> `{displayNames: Record<EnumChoice, string>}`
 *   * Anything else -> `never` (no UI schema supported); this is the leaves of the tree
 *
 * For example, if your step config shape is:
 * ```
 * {a: {kind: 'string'}, b: {kind: 'enum', choices: ['foo', 'bar']}}
 * ```
 * then the UI schema will be:
 *
 * ```ts
 * {
 *   a: {displayName: 'A display name'},
 *   b: {
 *     displayName: 'B display name',
 *     fieldSettings: {displayNames: {foo: 'Foo', bar: 'Bar'}} // all optional
 *   }
 * }
 * ```
 *
 * See more examples below in STEP_UI_SCHEMAS.
 */
export type StepTypeConfigUISchema<
  StepType extends PipelineStepType,
  StepVersion extends number,
> = GetPipelineStepShape<StepType, StepVersion>['config'] extends StepConfigShape
  ? _GetConfigShapeUISchema<GetPipelineStepShape<StepType, StepVersion>['config']>
  : never;

type _GetJsonSpecUISchemaShapeV2<JsonSpec extends PipelineConfigJsonSpec> = JsonSpec extends {
  kind: 'shape';
  fields: infer Fields extends Record<string, PipelineConfigJsonSpec>;
}
  ? {
      // Only variable names present in this array will be rendered.
      [FIELD_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<keyof Fields>;
      [SECTION_NAME_UI_SCHEMA_KEY]?: string;
    } & {
      [K in keyof Fields]: StandardComponentUISchemaV2<Fields[K]>;
    }
  : never;

export interface TabContainerUISchema {
  displayName: string;
  uiSchema: ShapeUISchemaV2;
}

/**
 * Tab order value that can be either a static array or a function that dynamically
 * determines tab order based on the current configuration.
 *
 * @example
 * // Static tab order
 * [TAB_ORDER_UI_SCHEMA_KEY]: ['configure', 'style']
 *
 * @example
 * // Dynamic tab order based on chart type
 * [TAB_ORDER_UI_SCHEMA_KEY]: (config) => {
 *   const chartType = config?.chartConfig?.type;
 *   return chartType === 'HEATMAP' ? ['configure'] : ['configure', 'style'];
 * }
 */
export type TabOrderValue =
  | ReadonlyArray<string>
  | ((config: Record<string, JSONValue>) => ReadonlyArray<string>);

export interface TabbedUISchema {
  [TAB_ORDER_UI_SCHEMA_KEY]: TabOrderValue;
  [HAS_BACKEND_FIELD_GENERATOR]?: boolean;
  [key: string]: TabContainerUISchema;
}

export type StepTypeConfigUISchemaV2<
  StepType extends PipelineStepType,
  StepVersion extends number,
> = GetPipelineStepShape<StepType, StepVersion>['config'] extends StepConfigShape
  ? ShapeUISchemaV2 | TabbedUISchema
  : never;

const AGGREGATION_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.AGGREGATE_DATASET][0].config.aggregations.elementSpec;

const WINDOW_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.WINDOW_FUNCTIONS][0].config.windows.elementSpec;

const FIND_AND_REPLACE_OPTIONS_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.FIND_AND_REPLACE][0].config.findAndReplaceOptions;

const AGGREGATE_TAG_KEY_DISPLAY_NAME_EXCEPTIONS: Record<string, string> = {
  STRING_AGG: 'Text concat',
};

/** @package */
export const AGGREGATE_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof AGGREGATION_ELEMENT_SHAPE>,
  string
> = objectFromEntries(
  AGGREGATION_ELEMENT_SHAPE.choices
    .map((choice) => choice.fields[AGGREGATION_ELEMENT_SHAPE.tagKey].value)
    .map((tagValue) => [
      tagValue,
      AGGREGATE_TAG_KEY_DISPLAY_NAME_EXCEPTIONS[tagValue] ?? capitalize(constantCaseToWords(tagValue)),
    ])
);

/** @package */
export const WINDOW_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof WINDOW_ELEMENT_SHAPE>,
  string
> = objectFromEntries(
  WINDOW_ELEMENT_SHAPE.choices
    .map((choice) => choice.fields[WINDOW_ELEMENT_SHAPE.tagKey].value)
    .map((tagValue) => [tagValue, capitalize(constantCaseToWords(tagValue))])
);

const AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES = {
  ...AGGREGATE_TAG_KEY_DISPLAY_NAMES,
  ...WINDOW_TAG_KEY_DISPLAY_NAMES,
};

const FUNCTIONS_WITHOUT_COLUMN = new Set([
  'COUNT',
  'CUME_DIST',
  'DENSE_RANK',
  'PERCENT_RANK',
  'RANK',
  'ROW_NUMBER',
]);

/** @package */
export function onChangeAggregationOrWindowMiddleware<
  T extends
    | PipelineJsonSpecToTsType<typeof AGGREGATION_ELEMENT_SHAPE, 'draft'>
    | PipelineJsonSpecToTsType<typeof WINDOW_ELEMENT_SHAPE, 'draft'>,
>(
  newConfig: T,
  oldConfig: T,
  dataFramesInfos: ConfigDataFramesInfos<
    PipelineStepType.AGGREGATE_DATASET | PipelineStepType.WINDOW_FUNCTIONS,
    0
  >
): T {
  if (newConfig?.aggregationFunction == null) {
    return newConfig;
  }
  if (FUNCTIONS_WITHOUT_COLUMN.has(newConfig.aggregationFunction)) {
    return {
      ...newConfig,
      newColumnName:
        newConfig.newColumnName ?? AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction],
    };
  } else if (
    // If aggregation function and column are set, _and_ one of those is being changed, then
    // set the new column name to <Aggregation fn>(<Column name>)
    // eslint-disable-next-line benchlint/no-in-operator
    'column' in newConfig &&
    newConfig.column != null &&
    (oldConfig?.aggregationFunction !== newConfig.aggregationFunction ||
      oldConfig?.column !== newConfig.column)
  ) {
    const newColumnName = `${AGGREGATE_OR_WINDOW_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction]} of ${
      findAndAssertColumn(dataFramesInfos['Input dataset'][0].dataFrameSpec, newConfig.column).displayName
    }`;
    // If the aggregation function is STRING_AGG, also set the default separator to an empty string
    return newConfig.aggregationFunction === 'STRING_AGG'
      ? {...newConfig, newColumnName, separator: newConfig.separator ?? ''}
      : {...newConfig, newColumnName};
  }
  return newConfig;
}

/** @package */
export const FIND_AND_REPLACE_TAG_KEY_DISPLAY_NAMES: Record<
  GetTaggedUnionTagValues<typeof FIND_AND_REPLACE_OPTIONS_SHAPE>,
  string
> = objectFromEntries([
  ['LITERAL', capitalize('Text')],
  ['REGEX', `${capitalize('Regular expressions')} (RE2 syntax)`], // Leave RE2 out so its not uncapitalized
]);

/** @package */
export function onChangeFindAndReplaceMiddleware<
  T extends PipelineJsonSpecToTsType<typeof FIND_AND_REPLACE_OPTIONS_SHAPE, 'draft'>,
>(newConfig: T, oldConfig: T, inputDataFrames: InputDataFrames<PipelineStepType.FIND_AND_REPLACE, 0>): T {
  if (newConfig?.findAndReplaceType == null) {
    return newConfig;
  }
  const sharedOptions = {
    matchAllOccurrences: false,
    replacementString: '',
  };
  if (newConfig?.findAndReplaceType === 'LITERAL') {
    return {
      literalString: '',
      matchWholeWord: true,
      ...sharedOptions,
      ...newConfig,
    };
  } else {
    return {
      patternString: '',
      ...sharedOptions,
      ...newConfig,
    };
  }
}

export const AGGREGATE_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.AGGREGATE_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['aggregations', 'groupColumns'],
  groupColumns: {displayName: 'Group by'},
  aggregations: {
    displayName: 'Aggregate',
    fieldSettings: {
      tagKeyDisplayNames: AGGREGATE_TAG_KEY_DISPLAY_NAMES,
      placeholder: 'Select function',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'newColumnName', 'separator', 'orderByColumns'],
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeAggregationOrWindowMiddleware,
      choiceSettings: {
        aggregationFunction: {displayName: 'Aggregation function'},
        column: {displayName: 'Column to aggregate'},
        newColumnName: {displayName: 'New column name'},
        separator: {displayName: 'Separator'},
        orderByColumns: {
          displayName: 'Order by',
          fieldSettings: {
            column: {displayName: 'Column'},
            direction: {
              displayName: 'Direction',
              fieldSettings: {
                placeholder: 'Select direction',
                defaultValue: 'ASCENDING',
                displayNames: {
                  ASCENDING: 'Ascending',
                  DESCENDING: 'Descending',
                },
              },
            },
          },
        },
      },
    },
  },
};

export const BIN_DATA_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.BIN_DATA, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'numBins'],
  column: {displayName: 'Column'},
  numBins: {displayName: 'Number of bins'},
};

export const BOLTZGEN_SUCCESS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.BOLTZGEN_SUCCESS, 0> = {};

export const CONVERT_ASM_TO_CSV_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.CONVERT_ASM_TO_CSV, 0> =
  {
    [HAS_BACKEND_FIELD_GENERATOR]: true,
    transformType: {
      displayName: 'Transform Type',
      fieldSettings: {
        placeholder: 'Select transform type',
      },
    },
  };

export const CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.CONVERT_FILE_TO_DATASET_WITH_DATA_ENTRY_ASSISTANT,
  0
> = {
  [BANNER_KEY]: {
    text: 'This feature is powered by AI, and may make mistakes. Always verify the accuracy of AI-generated content.',
    supportingInfo:
      'To generate the requested content, the AI system will analyze the content of the input file. For more information on AI security and privacy, [look here](https://help.benchling.com/hc/en-us/articles/20901323667853-Security-and-privacy-for-LLMs-and-AI-powered-features).',
  },
  datasetName: {
    displayName: 'Dataset Name',
  },
  instructions: {
    displayName: 'Add instructions',
    infoText:
      'Include any additional instructions or clarifications to help the AI system understand how to create the dataset',
  },
  columns: {
    displayName: 'Columns',
    infoText:
      'The column names and types to use for generation. If empty, the step will use AI to generate suggested columns.',
    fieldSettings: {
      name: {
        displayName: 'Name',
      },
      data_type: {
        displayName: 'Data Type',
      },
    },
  },
};

export const CONVERT_JSON_TO_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.CONVERT_JSON_TO_DATASET,
  0
> = {
  name: {
    displayName: 'Dataset Name',
  },
  columns: {
    displayName: 'Columns',
    fieldSettings: {
      name: {
        displayName: 'Column Name',
        infoText: "May include another column value in name via '$' substitution e.g.: 'ColName $unit$'.",
      },
      path: {
        displayName: 'JSON Path to Column Value',
        infoText: "Full path to the JSON leaf value to use for the column, e.g.: 'path/to/leaf'.",
      },
      include: {
        displayName: 'Include in dataset?',
        infoText: "May set to 'No' if using as part of another column name.",
      },
      required: {
        displayName: 'Required?',
        infoText: 'Dataset creation will fail if the JSON path is missing from the input.',
      },
    },
  },
};

export const CREATE_PROCEDURE_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.CREATE_PROCEDURE_DATASET,
  0
> = {
  // TODO(BNCH-135231): Polish UI schema based on product requirements
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['seedItems', 'datasetConfig'],
  seedItems: {
    displayName: 'Items to analyze',
    infoText: 'Select the type of item, and specify which items the dataset will be based on.',
    fieldSettings: {
      tagKeyDisplayNames: {
        STUDY: 'Studies',
        PROCEDURE_RUN: 'Runs',
        WORKSHEET: 'Worksheets',
      },
      placeholder: 'Select item type',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['seedItemIds'],
      choiceSettings: {
        seedItemType: {displayName: 'Item type'},
        seedItemIds: {displayName: 'Items'},
      },
    },
  },
  datasetConfig: {
    displayName: 'Dataset configuration',
    infoText: 'Select the type of dataset to create.',
    fieldSettings: {
      tagKeyDisplayNames: {
        RESULT_SCHEMA: 'Result schema',
        ENTITY_SCHEMA: 'Entity schema',
        CONDITIONS: 'Conditions',
        DATA_PARAMETER: 'Data parameter',
        MATERIAL_INPUT: 'Material input',
        MATERIAL_OUTPUT: 'Material output',
        EQUIPMENT: 'Equipment',
        TEST_ORDERS: 'Test orders',
        PREVIOUS_TEST_ORDERS: 'Previous test orders',
        PMEI_ASSOCIATION: 'PMEI association',
        INDIVIDUAL_PLATE_POSITIONS: 'Individual plate positions',
        INDIVIDUAL_PLATE_CONTENTS: 'Individual plate contents',
        PLATE_SUMMARY: 'Plate summary',
        PLATE_RECORD_POSITIONS: 'Plate record positions',
        PLATE_RECORD_CONTENTS: 'Plate record contents',
        PLATE_DESIGN_POSITIONS: 'Plate design positions',
        PLATE_DESIGN_CONTENTS: 'Plate design contents',
      },
      placeholder: 'Select dataset type',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['sourceObjectId'],
      [HIDDEN_FIELDS_KEY]: ['procedureDatasetVersion'],
      choiceSettings: {
        procedureDatasetType: {displayName: 'Dataset type'},
        sourceObjectId: {displayName: 'Schema ID'},
      },
    },
  },
};

export const EXTRACT_DATA_FRAME_VALUE_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.EXTRACT_DATA_FRAME_VALUE,
  0
> = {
  columnSystemName: {
    displayName: 'Column',
  },
  rowIndex: {
    displayName: 'Row Index',
    infoText: 'Index of the row to extract value from',
  },
};

export const MAP_JSON_TO_DATASETS_AND_FILES_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.MAP_JSON_TO_DATASETS_AND_FILES,
  0
> = {
  datasets: {
    displayName: 'Datasets',
    fieldSettings: {
      name: {
        displayName: 'Dataset Name',
      },
      columns: {
        displayName: 'Columns',
        fieldSettings: {
          name: {
            displayName: 'Column Name',
            infoText: "May include another column value in name via '$' substitution e.g.: 'ColName $unit$'.",
          },
          path: {
            displayName: 'JSON Path to Column Value',
            infoText: "Full path to the JSON leaf value to use for the column, e.g.: 'path/to/leaf'.",
          },
          include: {
            displayName: 'Include in dataset?',
            infoText: "May set to 'No' if using as part of another column name.",
          },
          required: {
            displayName: 'Required?',
            infoText: 'Dataset creation will fail if the JSON path is missing from the input.',
          },
        },
      },
      include: {
        displayName: 'Include in output?',
        infoText: "May set to 'No' if adding to another dataset via a join transform.",
      },
      is_metadata: {
        displayName: 'Output as metadata JSON file?',
        infoText:
          'If set, dataset will be output as a file containing a JSON blob. Must have a single value per column.',
      },
    },
  },
  transforms: {
    displayName: 'Transforms',
    fieldSettings: {
      placeholder: 'Select a transform',
      choiceSettings: {
        type: {
          displayName: 'Transform Type',
        },
        dataset_1: {
          displayName: 'Dataset 1',
        },
        dataset_2: {
          displayName: 'Dataset 2',
        },
        join_key_1: {
          displayName: 'Dataset 1 Join Column',
        },
        join_key_2: {
          displayName: 'Dataset 2 Join Column',
        },
        dataset: {
          displayName: 'Dataset',
        },
        path: {
          displayName: 'JSON Path to list to pivot on',
        },
        value_path: {
          displayName: 'JSON Path to leaf value to use for pivot column value',
        },
        label_path: {
          displayName: 'JSON Path to leaf value to use for pivot column name',
        },
      },
    },
  },
};

export const CONVERT_TO_ASM_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.CONVERT_TO_ASM, 0> = {
  [HAS_BACKEND_FIELD_GENERATOR]: true,
  asmVendor: {
    displayName: 'Instrument Vendor',
    fieldSettings: {
      placeholder: 'Select vendor',
    },
  },
  transformType: {
    displayName: 'CSV Transform Type',
    fieldSettings: {
      placeholder: 'Optionally select CSV transform type',
    },
    infoText: 'If specified, output file will be transformed CSV.',
  },
};

export const CREATE_DATASET_FROM_FILE_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.CREATE_DATASET_FROM_FILE,
  0
> = {
  datasetName: {displayName: 'Dataset name'},
  csvDelimiter: {
    displayName: 'CSV delimiter',
    fieldSettings: {
      defaultValue: ',',
    },
  },
  customCsvDelimiter: {
    displayName: 'Custom CSV delimiter',
    infoText: "Only used if 'Custom' is selected for CSV delimiter.",
  },
  dataLocale: {
    displayName: 'Data locale',
    fieldSettings: {
      defaultValue: 'en-US',
    },
  },
  sheetName: {
    displayName: 'Spreadsheet sheet name',
    infoText: 'Used to specify the sheet/tab to use for multi-sheet Excel files.',
  },
};

export const EXTRACT_OR_REMOVE_LINES_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.EXTRACT_OR_REMOVE_LINES,
  0
> = {
  actionType: {
    displayName: 'Action type',
    fieldSettings: {
      placeholder: 'Select action',
      defaultValue: 'EXTRACT_LINES',
    },
  },
  startLine: {displayName: 'Start line'},
  endLine: {displayName: 'End line'},
};

const FORMULA_ELEMENT_SHAPE = PIPELINE_STEP_SHAPES[PipelineStepType.COMPUTED_COLUMN][0].config.formula;
export const onChangeFormulaElementMiddleware: OnChangeConfigMiddlewareFn<
  typeof FORMULA_ELEMENT_SHAPE,
  PipelineStepType.COMPUTED_COLUMN,
  0
> = (newConfig, oldConfig, dataFramesInfos) => {
  if (newConfig.formulaText == null) {
    return {...newConfig, formulaAst: null};
  }
  try {
    return {...newConfig, formulaAst: formulaTextToFormulaAstJson(newConfig.formulaText)};
  } catch (e) {
    if (e instanceof AnalysisFormulaParsingError) {
      return {...newConfig, formulaAst: null};
    } else {
      throw e;
    }
  }
};

export const COMPUTED_COLUMN_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.COMPUTED_COLUMN, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['newColumnName', 'formula'],
  newColumnName: {displayName: 'New column name'},
  formula: {
    displayName: 'Formula text',
    [CUSTOM_COMPONENT_KEY]: AnalysisFormulaEditor,
    [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeFormulaElementMiddleware,
  },
};

const PIVOT_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.PIVOT_DATASET][0].config.valueColumns.elementSpec;

/** @package */
export const onChangePivotElementMiddleware: OnChangeConfigMiddlewareFn<
  typeof PIVOT_ELEMENT_SHAPE,
  PipelineStepType.PIVOT_DATASET,
  0
> = (newConfig, oldConfig, dataFramesInfos) => {
  if (newConfig?.aggregationFunction == null) {
    return newConfig;
  }
  if (
    // If newColumnName is not set, and a column is set, and one of aggregation functions or columns is being
    // changed, then set the new column name to <Aggregation fn>(<Column name>)
    newConfig.newColumnName == null &&
    newConfig.column != null &&
    (oldConfig?.aggregationFunction !== newConfig.aggregationFunction ||
      oldConfig?.column !== newConfig.column)
  ) {
    return {
      ...newConfig,
      newColumnName: `${AGGREGATE_TAG_KEY_DISPLAY_NAMES[newConfig.aggregationFunction]} of ${
        findAndAssertColumn(dataFramesInfos['Input dataset'][0].dataFrameSpec, newConfig.column).displayName
      }`,
    };
  }
  return newConfig;
};

export const PIVOT_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.PIVOT_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['pivotColumns', 'valueColumns', 'groupColumns'],
  pivotColumns: {displayName: 'Pivot columns'},
  valueColumns: {
    displayName: 'Values',
    fieldSettings: {
      tagKeyDisplayNames: AGGREGATE_TAG_KEY_DISPLAY_NAMES,
      placeholder: 'Select function',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'newColumnName'],
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangePivotElementMiddleware,
      choiceSettings: {
        aggregationFunction: {displayName: 'Aggregation function'},
        column: {displayName: 'Column to aggregate'},
        newColumnName: {displayName: 'New column name'},
      },
    },
  },
  groupColumns: {displayName: 'Pivot rows'},
};

export const RUN_HIGHRES_PROTOCOL_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.RUN_HIGHRES_PROTOCOL,
  0
> = {
  // TODO(BNCH-133099): Create custom component powered by HighRes data
  scientificProtocolId: {displayName: 'Scientific Protocol'},
  name: {displayName: 'Name'},
  requestedByUserId: {displayName: 'Requested by'},
  cellarioSystemId: {displayName: 'Cellario System'},
  ports: {
    displayName: 'Ports',
    fieldSettings: {
      portId: {displayName: 'Port'},
      labwareTypeId: {displayName: 'Labware Type'},
      plateIdColumn: {displayName: 'Plate Column'},
    },
  },
};

export const SORT_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.SORT_COLUMNS, 0> = {
  sortOptions: {
    displayName: 'Sort by',
    fieldSettings: {
      column: {displayName: 'Column'},
      direction: {
        displayName: 'Direction',
        fieldSettings: {
          placeholder: 'Select sort direction',
          defaultValue: 'ASCENDING',
          displayNames: {
            ASCENDING: 'Ascending',
            DESCENDING: 'Descending',
          },
        },
      },
    },
  },
};

export const UNPIVOT_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.UNPIVOT_DATASET, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['unpivotColumns', 'nameColumn', 'valueColumn'],
  [HIDDEN_FIELDS_KEY]: ['excludedColumns'],
  unpivotColumns: {displayName: 'Unpivot columns'},
  nameColumn: {displayName: 'Name column'},
  valueColumn: {displayName: 'Value column'},
};

export const WINDOW_FUNCTIONS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.WINDOW_FUNCTIONS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['windows', 'orderByColumns'],
  orderByColumns: {
    displayName: 'Order by',
    fieldSettings: {
      column: {displayName: 'Column'},
      direction: {
        displayName: 'Direction',
        fieldSettings: {
          placeholder: 'Select direction',
          defaultValue: 'ASCENDING',
          displayNames: {
            ASCENDING: 'Ascending',
            DESCENDING: 'Descending',
          },
        },
      },
    },
  },
  windows: {
    displayName: 'Windows',
    fieldSettings: {
      tagKeyDisplayNames: WINDOW_TAG_KEY_DISPLAY_NAMES,
      placeholder: 'Select function',
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'newColumnName'],
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeAggregationOrWindowMiddleware,
      choiceSettings: {
        aggregationFunction: {displayName: 'Window function'},
        column: {displayName: 'Column to aggregate'},
        newColumnName: {displayName: 'New column name'},
        partitionColumns: {displayName: 'Partition by'},
        orderByColumns: {
          displayName: 'Order by',
          fieldSettings: {
            column: {displayName: 'Column'},
            direction: {
              displayName: 'Direction',
              fieldSettings: {
                placeholder: 'Select direction',
                defaultValue: 'ASCENDING',
                displayNames: {
                  ASCENDING: 'Ascending',
                  DESCENDING: 'Descending',
                },
              },
            },
          },
        },
      },
    },
  },
};

export const FIND_AND_REPLACE_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.FIND_AND_REPLACE, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['selectedColumns', 'findAndReplaceOptions'],
  selectedColumns: {
    displayName: 'Columns',
    infoText:
      'Specify which text columns to find and replace values in. If no columns are selected, all text columns are included.',
  },
  findAndReplaceOptions: {
    displayName: 'Options',
    fieldSettings: {
      tagKeyDisplayNames: FIND_AND_REPLACE_TAG_KEY_DISPLAY_NAMES,
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'literalString',
        'patternString',
        'replacementString',
        'matchWholeWord',
        'matchAllOccurrences',
      ],
      choiceSettings: {
        findAndReplaceType: {displayName: 'Search with text or regular expressions?'},
        [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeFindAndReplaceMiddleware,
        literalString: {displayName: 'Search text'},
        patternString: {displayName: 'Regex pattern'},
        matchWholeWord: {displayName: 'Match whole word?'},
        matchAllOccurrences: {displayName: 'Match all occurrences within a cell?'},
        replacementString: {displayName: 'Replacing string'},
      },
    },
  },
};

export const HIDE_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.HIDE_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['columns'],
  columns: {
    displayName: 'Columns to hide',
  },
  columnsToKeep: {
    displayName: 'Columns to keep',
  },
};

export const RETYPE_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.RETYPE_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['retypeColumns'],
  retypeColumns: {
    displayName: 'Column formats',
    fieldSettings: {
      column: {displayName: 'Column'},
      type: {
        displayName: 'Data format',
        fieldSettings: {
          defaultValue: 'INT',
          displayNames: TYPES_TO_DISPLAY_NAMES,
        },
      },
    },
  },
};

export const RENAME_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.RENAME_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['renameColumns'],
  renameColumns: {
    displayName: 'Columns',
    fieldSettings: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['column', 'name'],
      column: {displayName: 'Column'},
      name: {
        displayName: 'New name',
      },
    },
  },
};

const REORDER_COLUMNS_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.REORDER_COLUMNS][0].config.reorderColumns;
export const onChangeReorderColumnsMiddleware: OnChangeConfigMiddlewareFn<
  typeof REORDER_COLUMNS_ELEMENT_SHAPE,
  PipelineStepType.REORDER_COLUMNS,
  0
> = (newConfig, oldConfig, dataFramesInfos) => {
  if (newConfig.length === 0 && oldConfig.length === 0) {
    return dataFramesInfos['Input dataset'][0].dataFrameSpec.columns.map((column) => column.systemName);
  }
  return newConfig;
};

export const REORDER_COLUMNS_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.REORDER_COLUMNS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['reorderColumns'],
  reorderColumns: {
    displayName: 'Columns',
    [CUSTOM_COMPONENT_KEY]: ColumnDragAndDrop,
    [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeReorderColumnsMiddleware,
  },
};

export const PARAMETER_OPTIMIZATION_CONFIGURATION_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['labelColumn', 'groupingColumn', 'featureColumns', 'excludedColumns'],
  [HIDDEN_FIELDS_KEY]: ['metadataColumns'],
  labelColumn: {displayName: 'Y variable', infoText: 'The column to optimize.'},
  groupingColumn: {
    displayName: 'Grouping column',
    infoText: 'The column that signifies batch or technical replicate to prevent overfitting.',
  },
  featureColumns: {
    displayName: 'Feature columns',
    infoText:
      'If no columns are selected, all columns except for the Y variable and Grouping column are used as features.',
  },
  excludedColumns: {
    displayName: 'Excluded columns',
    infoText: 'Columns that should not be used as features. These columns will be dropped from modeling.',
  },
};

const IS_INPUT_X_ELEMENT_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.REGRESSION_INTERPOLATION][0].config.isInputX;
export const onChangeIsInputXMiddleware: OnChangeConfigMiddlewareFn<
  typeof IS_INPUT_X_ELEMENT_SHAPE,
  PipelineStepType.REGRESSION_INTERPOLATION,
  0
> = (newConfig, oldConfig, inputDataFrames) => newConfig;

export const REGRESSION_INTERPOLATION_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.REGRESSION_INTERPOLATION,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['inputColumn', 'isInputX', 'inputPartitionColumn'],
  [HIDDEN_FIELDS_KEY]: ['selectedPartitionValue'],
  inputColumn: {displayName: 'Input column'},
  isInputX: {
    displayName: 'Select prediction mode',
    infoText:
      'Whether the input column values will be treated as values of X or values of Y when calculating the interpolated values.',
    [CUSTOM_COMPONENT_KEY]: IsInputXToggleGroup,
    [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeIsInputXMiddleware,
  },
  inputPartitionColumn: {
    displayName: 'Input series column (optional)',
    infoText:
      "If specified, then the parameter table (regression model) and the input table will be joined based on this column's values.",
  },
};

/**
 * Middleware function that automatically updates chart series colors when the discrete color palette changes.
 *
 * This function serves as a configuration change handler that ensures visual consistency
 * when users switch between different color palettes (e.g., BENCHLING, DARK24, etc.).
 * When the discreteColorPalette field is modified, this middleware regenerates colors
 * for all existing series in the chart configuration, cycling through the new palette's
 * available colors to maintain unique color assignments.
 *
 * @param newValue - The new discrete color palette name (e.g., 'BENCHLING', 'DARK24')
 * @param path - The configuration path being modified (must be ['chartConfig', 'parameters', 'discreteColorPalette'])
 * @param oldConfig - The current pipeline step configuration containing chart settings
 * @param dataFramesInfos - DataFrame information (unused but required by middleware interface)
 * @returns Updated configuration with regenerated series colors, or original config if no changes needed
 *
 * @example
 * // When user changes palette from 'BENCHLING' to 'DARK24':
 * // - All existing series colors are reassigned using DARK24 palette colors
 * // - Colors cycle through palette if more series than available colors
 * // - Original series order and other properties are preserved
 *
 * @package
 */
export function onChangeDiscreteColorPaletteMiddleware(
  newValue: string,
  path: ConfigShapePath,
  oldConfig: Record<string, JSONValue>,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
): Record<string, JSONValue> {
  const isChangingPalette =
    JSON.stringify(path) === JSON.stringify(['chartConfig', 'parameters', 'discreteColorPalette']);

  if (isChangingPalette) {
    const paletteName: DiscreteColorPalette = isDiscreteColorPalette(newValue) ? newValue : 'BENCHLING';
    const palette = COLOR_PALETTES[paletteName];

    const chartConfig = oldConfig?.chartConfig;
    assertJSONObject(chartConfig);
    const parameters = chartConfig.parameters;
    assertJSONObject(parameters);
    const seriesColorConfigs = parameters.seriesColorConfigs;

    if (!isJSONArray(seriesColorConfigs) || seriesColorConfigs.length === 0) {
      return oldConfig;
    }

    const newSeriesColorConfigs = seriesColorConfigs.map(
      (mapping: JSONObject, idx: number): JSONObject => ({
        ...mapping,
        color: palette[idx % palette.length],
      })
    );

    return {
      ...oldConfig,
      chartConfig: {
        ...chartConfig,
        parameters: {
          ...parameters,
          discreteColorPalette: newValue,
          seriesColorConfigs: newSeriesColorConfigs,
        },
      },
    };
  }

  return oldConfig;
}

/**
 * Generates a default color configuration for a chart series based on the current palette and index.
 *
 * This function is used as a FIELD_DEFAULTS_GENERATOR_KEY to automatically assign colors
 * to chart series when new series are added or when the color palette changes. It cycles
 * through the available colors in the selected palette, ensuring each series gets a unique
 * color when possible.
 *
 * @param config - The pipeline step configuration containing chart settings
 * @param dataFramesInfos - DataFrame information (unused in this implementation but required by the generator interface)
 * @param idx - The index of the series (0-based), used to cycle through palette colors
 * @returns A JSONObject containing the color configuration with a 'color' property set to a hex color value
 *
 * @example
 * // For a series at index 0 with BENCHLING palette
 * getDefaultSeriesColorConfig(config, dataFramesInfos, 0)
 * // Returns: { color: "#000DB5" } // First color in the BENCHLING palette
 *
 */
export function getDefaultSeriesColorConfig(
  config: JSONObject,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>,
  idx = 0
): JSONObject {
  const chartConfig = config.chartConfig;
  assertJSONObject(chartConfig);

  const parameters = chartConfig.parameters;
  assertJSONObject(parameters);

  // Check if the incoming palette is valid, if not, default to BENCHLING
  const discreteColorPalette: DiscreteColorPalette = isDiscreteColorPalette(parameters?.discreteColorPalette)
    ? parameters.discreteColorPalette
    : 'BENCHLING';
  const paletteHexColors = COLOR_PALETTES[discreteColorPalette];
  return {
    color: paletteHexColors[idx % paletteHexColors.length],
  };
}

export const CHART_VIEW_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.CHART_VIEW, 0> = {
  [TAB_ORDER_UI_SCHEMA_KEY]: (config) => {
    const chartConfig = config.chartConfig;
    assertJSONObject(chartConfig);
    const chartType = chartConfig.type;
    // Hide style tab for HEATMAP since it has no style options yet
    return chartType === 'HEATMAP' ? ['configure'] : ['configure', 'style'];
  },
  configure: {
    displayName: 'Configure',
    uiSchema: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'boxPlotConfigure',
        'pieChartConfigure',
        'lineChartConfigure',
        'heatMapConfigure',
        'scatterChartConfigure',
        'barChartConfigure',
      ],
      boxPlotConfigure: createChartTypeSchema('BOX_PLOT', BOX_PLOT_CONFIG_SECTIONS),
      pieChartConfigure: createChartTypeSchema('PIE_CHART', PIE_CHART_CONFIG_SECTIONS),
      lineChartConfigure: createChartTypeSchema('LINE_CHART', LINE_CHART_CONFIG_SECTIONS),
      heatMapConfigure: createChartTypeSchema('HEATMAP', HEATMAP_CONFIG_SECTIONS),
      scatterChartConfigure: createChartTypeSchema('SCATTER_PLOT', SCATTER_CHART_CONFIG_SECTIONS),
      barChartConfigure: createChartTypeSchema('BAR_CHART', BAR_CHART_CONFIG_SECTIONS),
    },
  },
  style: {
    displayName: 'Style',
    uiSchema: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: [
        'boxPlotStyle',
        'pieChartStyle',
        'lineChartStyle',
        'scatterChartStyle',
        'barChartStyle',
      ],
      boxPlotStyle: createChartTypeSchema('BOX_PLOT', BOX_PLOT_STYLE_SECTIONS),
      pieChartStyle: createChartTypeSchema('PIE_CHART', PIE_CHART_STYLE_SECTIONS),
      lineChartStyle: createChartTypeSchema('LINE_CHART', LINE_CHART_STYLE_SECTIONS),
      scatterChartStyle: createChartTypeSchema('SCATTER_PLOT', SCATTER_CHART_STYLE_SECTIONS),
      barChartStyle: createChartTypeSchema('BAR_CHART', BAR_CHART_STYLE_SECTIONS),
    },
  },
};

export const FILTER_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.FILTER_DATASET, 0> = {
  filter: {
    displayName: 'Column filters',
    [CUSTOM_COMPONENT_KEY]: FilterStepFilterConfig,
  },
};

export const SEND_TO_CONNECTION_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.SEND_TO_CONNECTION, 0> =
  {
    connectionId: {
      displayName: 'Connection',
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <SearchEditor {...props} modelTypes={[ModelTypeConstants.CONNECTION]} />
      ),
    },
  };

export const USER_DEFINED_FUNCTION_V0_UI_SCHEMA: StepTypeConfigUISchema<
  PipelineStepType.USER_DEFINED_FUNCTION,
  0
> = {
  userDefinedCode: {
    displayName: 'Code',
    [CUSTOM_COMPONENT_KEY]: UserDefinedFunctionEditor,
  },
  [HIDDEN_FIELDS_KEY]: ['params'],
};

export const DATA_CATALOG_FILE_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.DATA_CATALOG_FILE, 0> = {
  fileId: {
    displayName: 'File',
  },
  fileName: {
    displayName: 'File name',
  },
};

export const DATA_CATALOG_FILES_V0_UI_SCHEMA: StepTypeConfigUISchema<PipelineStepType.DATA_CATALOG_FILES, 0> =
  {
    fileIds: {
      displayName: 'Files',
    },
    fileBaseName: {
      displayName: 'Base file name',
      infoText:
        'When multiple files are selected, this will be prepended to the original file name. If only one file is selected, this will be the name of the file.',
    },
  };

export const LOOK_UP_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.LOOK_UP, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['lookupConfig', 'schemaId', 'dataFrameName'],
  lookupConfig: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['lookupConfig'],
    lookupConfig: {
      [CUSTOM_COMPONENT_KEY]: LookUpConfigSectionEditor,
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
  schemaId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['schemaId'],
    schemaId: {
      displayName: BenchlingConfig.DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS
        ? 'Select entity or plate schema'
        : 'Select entity schema',
      infoText:
        "Choose the specific schema that matches the type of items in your lookup column. If the schema doesn't match your data, the step may fail or return no results.",
      [CUSTOM_COMPONENT_KEY]: LookUpFieldEditor,
    },
  },
};

export const JOIN_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.JOIN, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['join', 'dataFrameName'],
  join: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['join'],
    join: {[CUSTOM_COMPONENT_KEY]: JoinEditor},
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const UNION_DATASETS_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<PipelineStepType.UNION_DATASETS, 0> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['union', 'includeDuplicates', 'dataFrameName'],
  union: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['union'],
    union: {[CUSTOM_COMPONENT_KEY]: UnionEditor},
  },
  includeDuplicates: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['includeDuplicates'],
    includeDuplicates: {
      displayName: 'Include duplicate rows',
      configShapePath: ['includeDuplicates'],
    },
  },
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Name table',
      configShapePath: ['dataFrameName'],
    },
  },
};

export const DEFINE_TEMPLATE_VARIABLES_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['variables'],
  variables: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['variables'],
    variables: {
      displayName: 'Define variables',
      [CUSTOM_COMPONENT_KEY]: DefineTemplateVariablesEditor,
    },
  },
};
